/**
 * Where to put a chart's hover readout so it never hides what you are pointing at.
 *
 * Two rules, in order of importance.
 *
 * Horizontal is the guarantee. Every cursor dot in a stacked chart sits on the
 * crosshair, so a tip centred on the crosshair covers all of them at once. Put
 * it wholly to one side and no dot can ever be behind it, however many panels
 * there are.
 *
 * Vertical is the courtesy. Given the room, the tip also steps off the band the
 * pointer is in, so the panel being read stays clear. This one cannot always be
 * honoured: a four-panel buoy readout runs over half the height of the chart, so
 * a middle panel has space neither above nor below. That is why it is not the
 * rule the guarantee rests on.
 */

export interface TipPlacement {
  /** Crosshair position, in the wrapper's own coordinates. */
  crosshairX: number;
  /** The band the pointer is in — the tip steps off this if it can. */
  bandTop: number;
  bandBottom: number;
  wrapWidth: number;
  wrapHeight: number;
  tipWidth: number;
  tipHeight: number;
  gap?: number;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(v, Math.max(lo, hi)));
}

export function placeTip(p: TipPlacement): { left: number; top: number } {
  const gap = p.gap ?? 10;

  // Prefer the right of the crosshair, fall back to the left, and if the tip is
  // wider than either side take the roomier one — clipping at the edge still
  // beats sitting on the dot.
  let left: number;
  if (p.crosshairX + gap + p.tipWidth <= p.wrapWidth) {
    left = p.crosshairX + gap;
  } else if (p.crosshairX - gap - p.tipWidth >= 0) {
    left = p.crosshairX - gap - p.tipWidth;
  } else {
    left = p.wrapWidth - p.crosshairX >= p.crosshairX ? p.wrapWidth - p.tipWidth : 0;
  }

  let top: number;
  if (p.bandBottom + gap + p.tipHeight <= p.wrapHeight) {
    top = p.bandBottom + gap;
  } else if (p.bandTop - gap - p.tipHeight >= 0) {
    top = p.bandTop - gap - p.tipHeight;
  } else {
    // No room either side of the band. Sit level with it: the horizontal offset
    // already keeps the dots visible, so this only has to stay on screen.
    top = p.bandTop;
  }

  return {
    left: clamp(left, 0, p.wrapWidth - p.tipWidth),
    top: clamp(top, 0, p.wrapHeight - p.tipHeight),
  };
}
